import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { API } from "@/lib/api";

const EMPTY = { sender_name: "", friend_name: "", friend_email: "", note: "" };
const inputCls = "rounded-full px-5 py-6 bg-white border-border";

export const FriendReferralForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals/friend-invite`, {
        sender_name: form.sender_name.trim(),
        friend_name: form.friend_name.trim() || undefined,
        friend_email: form.friend_email.trim(),
        note: form.note.trim() || undefined,
        page_path: window.location.pathname,
      });
      setSentTo(form.friend_name.trim() || form.friend_email.trim());
      setForm(EMPTY);
      toast.success("Your invite is on its way!");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Please check the details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sentTo) {
    return (
      <div className="mt-8 flex items-start gap-3 rounded-2xl bg-green-50 border border-green-200 p-5" data-testid="friend-referral-success">
        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-green-900">Invite sent to {sentTo}!</p>
          <button type="button" onClick={() => setSentTo(null)} className="text-sm text-green-800 underline mt-1" data-testid="friend-referral-another-btn">
            Invite another friend
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 rounded-2xl bg-white border border-border/40 p-5 md:p-6" data-testid="friend-referral-form">
      <p className="font-serif text-lg font-semibold text-foreground">Invite a friend by email</p>
      <p className="text-sm text-muted-foreground mt-1">We&rsquo;ll send them a warm, one-time personal invite with a link to this page.</p>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input required maxLength={80} placeholder="Your first name" value={form.sender_name} onChange={set("sender_name")} className={inputCls} data-testid="friend-referral-sender-input" />
        <Input maxLength={80} placeholder="Friend's first name (optional)" value={form.friend_name} onChange={set("friend_name")} className={inputCls} data-testid="friend-referral-friend-name-input" />
        <Input required type="email" placeholder="Friend's email" value={form.friend_email} onChange={set("friend_email")} className={`${inputCls} sm:col-span-2`} data-testid="friend-referral-friend-email-input" />
        <Textarea maxLength={500} rows={3} placeholder="Add a personal note (optional)" value={form.note} onChange={set("note")} className="sm:col-span-2 rounded-2xl px-5 py-3 bg-white border-border" data-testid="friend-referral-note-input" />
      </div>
      <Button type="submit" disabled={submitting} className="mt-4 rounded-full w-full sm:w-auto px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="friend-referral-submit-btn">
        {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
        {submitting ? "Sending..." : "Send Personal Invite"}
      </Button>
    </form>
  );
};
